import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronRight,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import siteConfig from "../../config/siteConfig";
import { useCommerce } from "../../hooks/useCommerce";
import Accordion from "../common/Accordion";
import BrandMark from "../common/BrandMark";
import classNames from "../common/classNames";
import { trapTabKey } from "../common/focusTrap";
import AnnouncementBar from "./AnnouncementBar";
import MegaMenu from "./MegaMenu";
import SearchOverlay from "./SearchOverlay";
import UtilityBar from "./UtilityBar";

const EMPTY_PRODUCTS = [];

const getImageSource = (image) =>
  typeof image === "string" ? image : image?.src || image?.url;

const getSearchSuggestions = (products, query, limit = 5) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length < 2) return [];

  return products
    .filter((product) =>
      [
        product.name,
        product.category,
        product.subtitle,
        ...(Array.isArray(product.tags) ? product.tags : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    )
    .slice(0, limit);
};

const MobileMenu = ({ open, onClose, items }) => {
  const closeButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    document.body.classList.add("has-open-menu");
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      else trapTabKey(event, drawerRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("has-open-menu");
      document.removeEventListener("keydown", handleKeyDown);
      returnFocusRef.current?.focus?.();
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="mobile-menu" role="presentation">
      <button
        className="mobile-menu__backdrop"
        type="button"
        onClick={onClose}
        aria-label="Close menu"
      />
      <aside
        ref={drawerRef}
        className="mobile-menu__drawer"
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        tabIndex="-1"
      >
        <header className="mobile-menu__header">
          <BrandMark showTagline />
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FiX aria-hidden="true" />
          </button>
        </header>
        <nav className="mobile-menu__nav" aria-label="Mobile main navigation">
          <ul>
            {items.map((item) => {
              const children = item.children || [];
              if (!children.length) {
                return (
                  <li key={`${item.label}-${item.href}`}>
                    <Link to={item.href || "#"} onClick={onClose}>
                      {item.label}
                      <FiChevronRight aria-hidden="true" />
                    </Link>
                  </li>
                );
              }

              return (
                <li key={`${item.label}-${item.href}`}>
                  <Accordion title={item.label} headingLevel={2}>
                    <Link
                      className="mobile-menu__all-link"
                      to={item.href || "/shop"}
                      onClick={onClose}
                    >
                      Shop all {item.label}
                    </Link>
                    <ul>
                      {children.map((child) => (
                        <li key={`${child.label}-${child.href}`}>
                          <Link to={child.href || "#"} onClick={onClose}>
                            {child.label}
                          </Link>
                          {child.children?.length > 0 && (
                            <ul>
                              {child.children.map((grandchild) => (
                                <li
                                  key={`${grandchild.label}-${grandchild.href}`}
                                >
                                  <Link
                                    to={grandchild.href || "#"}
                                    onClick={onClose}
                                  >
                                    {grandchild.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Accordion>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="mobile-menu__account-links">
          <Link
            to={siteConfig.routes?.wishlist || "/wishlist"}
            onClick={onClose}
          >
            <FiHeart aria-hidden="true" /> Wishlist
          </Link>
        </div>
        <div className="mobile-menu__contact">
          <p>Need a hand?</p>
          <a href={`mailto:${siteConfig.contact?.email}`}>
            {siteConfig.contact?.email}
          </a>
          <span>{siteConfig.contact?.hours}</span>
        </div>
      </aside>
    </div>
  );
};

const Header = ({ products = EMPTY_PRODUCTS, className }) => {
  const commerce = useCommerce();
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchBoxRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const availableProducts = useMemo(
    () => (products.length ? products : commerce.products || EMPTY_PRODUCTS),
    [commerce.products, products],
  );
  const suggestions = useMemo(
    () => getSearchSuggestions(availableProducts, query),
    [availableProducts, query],
  );
  const cartItems = commerce.cartItems || commerce.cart || [];
  const wishlistItems = commerce.wishlistItems || commerce.wishlist || [];
  const cartCount =
    commerce.cartCount ??
    cartItems.reduce((total, item) => total + (Number(item.quantity) || 1), 0);
  const wishlistCount = commerce.wishlistCount ?? wishlistItems.length;

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
    setSearchFocused(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;
    setSearchFocused(false);
    navigate(`/search?q=${encodeURIComponent(normalizedQuery)}`);
  };

  const closeSuggestionsOnBlur = (event) => {
    if (!searchBoxRef.current?.contains(event.relatedTarget))
      setSearchFocused(false);
  };

  const openCart = () => {
    if (commerce.openCart) commerce.openCart();
    else if (commerce.openCartDrawer) commerce.openCartDrawer();
    else if (commerce.openDrawer) commerce.openDrawer("cart");
    else navigate(siteConfig.routes?.cart || "/cart");
  };

  return (
    <header className={classNames("site-header", className)}>
      <UtilityBar />
      <div className="site-header__main">
        <div className="site-container site-header__main-inner">
          <button
            className="site-header__mobile-action site-header__menu-button"
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileMenuOpen(true)}
          >
            <FiMenu aria-hidden="true" />
            <span className="visually-hidden">Open menu</span>
          </button>

          <BrandMark className="site-header__brand" showTagline />

          <div
            ref={searchBoxRef}
            className="site-header__search-wrap"
            onFocus={() => setSearchFocused(true)}
            onBlur={closeSuggestionsOnBlur}
          >
            <form
              className="site-header__search"
              role="search"
              onSubmit={submitSearch}
            >
              <label className="visually-hidden" htmlFor="site-header-search">
                Search products
              </label>
              <input
                id="site-header-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search spices, seasonings and more"
                autoComplete="off"
              />
              <button type="submit" aria-label="Search">
                <FiSearch aria-hidden="true" />
              </button>
            </form>
            {searchFocused && query.trim().length >= 2 && (
              <div
                className="site-header__suggestions"
                id="header-search-suggestions"
                aria-live="polite"
              >
                {suggestions.length ? (
                  <>
                    <ul>
                      {suggestions.map((product) => {
                        const image = getImageSource(
                          product.images?.[0] || product.image,
                        );
                        return (
                          <li key={product.id || product.slug}>
                            <Link to={`/products/${product.slug}`}>
                              {image && (
                                <img src={image} alt="" loading="lazy" />
                              )}
                              <span>
                                <strong>{product.name}</strong>
                                {product.category && (
                                  <small>{product.category}</small>
                                )}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                    <button
                      className="site-header__view-results"
                      type="button"
                      onClick={submitSearch}
                    >
                      View all results <FiChevronRight aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <p>No products match “{query.trim()}”.</p>
                )}
              </div>
            )}
          </div>

          <div className="site-header__service">
            <span>Customer care</span>
            <a href={`mailto:${siteConfig.contact?.email}`}>
              {siteConfig.contact?.email}
            </a>
            <small>{siteConfig.contact?.hours}</small>
          </div>

          <nav
            className="site-header__actions"
            aria-label="Account and shopping tools"
          >
            <Link
              to={siteConfig.routes?.wishlist || "/wishlist"}
              aria-label={`Wishlist, ${wishlistCount} items`}
            >
              <span className="site-header__action-icon">
                <FiHeart aria-hidden="true" />
                {wishlistCount > 0 && (
                  <span className="site-header__count">{wishlistCount}</span>
                )}
              </span>
              <span>Wishlist</span>
            </Link>
            <button
              type="button"
              onClick={openCart}
              aria-haspopup="dialog"
              aria-label={`Cart, ${cartCount} items`}
            >
              <span className="site-header__action-icon">
                <FiShoppingBag aria-hidden="true" />
                {cartCount > 0 && (
                  <span className="site-header__count">{cartCount}</span>
                )}
              </span>
              <span>Cart</span>
            </button>
          </nav>
        </div>
      </div>
      <MegaMenu items={siteConfig.navigation || []} />
      <AnnouncementBar />
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        items={siteConfig.navigation || []}
      />
      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        products={availableProducts}
      />
    </header>
  );
};

export default Header;
